import { action, observable, runInAction } from 'mobx';

import cache from 'bz-stores/lib/diary-cache-store';
import view from 'bz-stores/lib/diary-view-store';
import Locks from 'bz-stores/lib/locks';
import BaseStore from 'bz-stores/mobx/base-store';
import {
  type AppointmentResponse,
  DiaryListRequest,
  DiaryListResponse,
  type GeneralCustomerResponse
} from 'tm3-core/api.dtos';

export enum EActivePopover {
  cancellation = 'cancellation',
  doNext = 'doNext',
  none = 'none',
  quickbill = 'quickbill',
  event = 'event'
}

export interface ICancellationOptions {
  id: number;
  mandatory: boolean;
  deleteCase: boolean;
}

export interface IQuickbillOptions {
  id: number;
  status: number;
  deleteCase: boolean;
  mandatory: boolean;
}

class DiaryPopoverStore {
  @observable activePopover: EActivePopover = EActivePopover.none;
  @observable anchorEl: HTMLElement | null = null;
  @observable selected = null;
  @observable appointmentDoNext = null;
  @observable patientDoNext = null;
  @observable quickbillOptions: Partial<IQuickbillOptions> = {};
  @observable cancellationOptions: Partial<ICancellationOptions> = {};
  @observable callback?: (() => void) | null = null;
  @action
  showPopoverForCustomer(appt: AppointmentResponse, customer: GeneralCustomerResponse) {
    const {
      reference,
      mobileTelephone,
      email,
      smsEnabled,
      title,
      forename,
      surname,
      gender,
      insuranceNumber,
      groupReference,
      status,
      registrationDate
    } = customer;
    const mapped = {
      customerReference: reference,
      customerMobileTelephone: mobileTelephone,
      customerEmail: email,
      customerSMSEnabled: smsEnabled,
      customerTitle: title,
      customerForename: forename,
      customerSurname: surname,
      customerGender: gender,
      insuranceNumber: insuranceNumber,
      groupReference: groupReference,
      customerStatus: status,
      customerRegistrationDate: registrationDate
    };
    return this.showPopover(EActivePopover.event, new DiaryListResponse({ ...mapped, ...appt }));
  }

  @action
  showPopover(
    activePopover: EActivePopover,
    appt: any,
    selection?: HTMLElement,
    callback?: () => void
  ) {
    let { allRooms, allLocations, allPractitioners } = cache;
    appt.room = allRooms.find((x) => x.id === appt.roomId);
    appt.location = allLocations.find((x) => x.id === appt.locationId);
    appt.practitioner = allPractitioners.find((x) => x.id === appt.practitionerId);
    appt.apptStockColour = appt.hexColour;

    if (selection) this.anchorEl = selection;
    this.activePopover = activePopover;
    this.selected = appt;
    this.callback = callback;
  }

  @action.bound
  hidePopover() {
    let _active = this.activePopover;
    let promise = !!this.selected
      ? Locks.actions.releaseLock('A', this.selected.id)
      : Promise.resolve();

    promise.finally(() => {
      this.selected = null;
      if (_active !== this.activePopover) {
        return;
      }

      this.callback?.();

      runInAction(() => {
        this.activePopover = EActivePopover.none;
        this.anchorEl = null;
        this.selected = null;
        this.callback = null;
      });
    });
  }

  @action
  doNextPrompt(appointment, patient) {
    if (!view.showDoNextScreen) return;
    // appointment = (@get('appointments') || []).filter((x) -> x.id is parseInt(apptId))
    // return unless appointment[0]?
    this.appointmentDoNext = appointment;
    this.patientDoNext = patient;
    this.activePopover = EActivePopover.doNext;
  }

  @action
  quickbill(id: number, status: number, deleteCase: boolean, mandatory?: boolean) {
    this.activePopover = EActivePopover.quickbill;
    this.quickbillOptions = { id, status, deleteCase, mandatory };
  }

  @action
  cancellation(id: number, mandatory: boolean, deleteCase: boolean) {
    this.activePopover = EActivePopover.cancellation;
    this.cancellationOptions = { id, mandatory, deleteCase };
  }
}

export default new DiaryPopoverStore();
